<script setup>
import AppConfig from '@/layout/common/AppConfig.vue';
import CompactLanguageSwitcher from '@/components/ui/CompactLanguageSwitcher.vue';
import { useLayout } from '@/layout/common/composables/layout';
import { useDirection } from '@/composables/useDirection';
import { useNotifications } from '@/composables/useNotifications_old.js';
import { useAuthStore } from '@/stores/auth';
import { useLanguageStore } from '@/stores/language';
import { useMenuItems } from '@/layout/main/menu';
import ProjectService from '@/service/Project/ProjectService.js';
import ProgramService from '@/service/Project/ProgramService.js';
// Strategic Plans global search is intentionally disabled for now.
// import StrategicPlanService from '@/service/StrategicPlanService.js';
import { routePermissions, routePermissionRoleFallbacks } from '@/router/routePermissions.js';
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import NotificationsDropdown from './NotificationsDropdown.vue';
import ProfileDropdown from './ProfileDropdown.vue';
import RightMenu from './RightMenu.vue';
import DashboardBreadcrumb from './DashboardBreadcrumb.vue';
import defaultAvatar from '@/assets/images/favicon/favicon-green-200.png';

const router = useRouter();
const search = ref('');
const headerRef = ref(null);
const searchContainerRef = ref(null);
const searchInputRef = ref(null);
const searchFocused = ref(false);
const activeSearchIndex = ref(-1);
const dataSearchLoading = ref(false);
const dataSearchResults = ref([]);
const searchDebounce = ref(null);
const isScrolled = ref(false);
let scrollContainer = null;
let searchRequestId = 0;
const authStore = useAuthStore();
const { t } = useLanguageStore();
const { unreadCount, fetchUnreadCount, startRealtime, stopRealtime } = useNotifications();
const { menuItems, bottomMenuItems } = useMenuItems();

const { isRtl, spacing, getDirectionClasses } = useDirection();

const { layoutConfig, isDarkTheme, toggleConfigSidebar, calculatedMenuMode, toggleMenuState } = useLayout();
const toggleDarkTheme = () => {
    layoutConfig.darkTheme = !layoutConfig.darkTheme;
};
const rightMenuOpen = ref(false);

const normalizeSearch = (value) => String(value || '').trim().toLowerCase();

const canAccessRoute = (routeName) => {
    if (authStore.hasAnyRole(['Super Admin', 'Admin'])) return true;

    const fallbackRoles = routePermissionRoleFallbacks[routeName];
    if (fallbackRoles && authStore.hasAnyRole(fallbackRoles)) return true;

    const required = routePermissions[routeName];
    if (!required) return true;

    return Array.isArray(required)
        ? authStore.hasAnyPermission(required)
        : authStore.hasPermission(required);
};

const flattenMenuItems = (items, parents = []) => {
    return items.flatMap((item) => {
        const label = item.label || '';
        const nextParents = label ? [...parents, label] : parents;
        const current = item.to
            ? [{
                label,
                path: item.to,
                icon: item.icon || 'pi pi-search',
                section: parents.join(' / '),
                meta: t('Page')
            }]
            : [];

        return [
            ...current,
            ...(Array.isArray(item.items) ? flattenMenuItems(item.items, nextParents) : [])
        ];
    });
};

const searchTargets = computed(() => flattenMenuItems([...menuItems.value, ...bottomMenuItems.value]));
const canSearchProjects = computed(() => canAccessRoute('projects') && canAccessRoute('project-detail'));
const canSearchPrograms = computed(() => canAccessRoute('programs') && canAccessRoute('program-detail'));
// const canSearchStrategy = computed(() => canAccessRoute('strategic-plans') && canAccessRoute('strategic-plan-detail'));

const pageSearchResults = computed(() => {
    const query = normalizeSearch(search.value);
    if (!query) return [];

    return searchTargets.value
        .filter((item) => {
            const haystack = normalizeSearch(`${item.label} ${item.path} ${item.section}`);
            return haystack.includes(query);
        })
        .slice(0, 8);
});

const filteredSearchResults = computed(() => [
    ...dataSearchResults.value,
    ...pageSearchResults.value
].slice(0, 10));

const showSearchResults = computed(() => searchFocused.value && search.value.trim().length > 0);

const getResponseItems = (response) => {
    const data = response?.data?.data || response?.data || [];
    return Array.isArray(data) ? data : [];
};

const buildRecordResult = ({ type, label, path, icon, item }) => ({
    type,
    label,
    path,
    icon,
    section: type,
    meta: [item.code, item.status].filter(Boolean).join(' · ')
});

const searchDataRecords = async (query, requestId) => {
    const requests = [];

    if (canSearchProjects.value) {
        requests.push(
            ProjectService.getProjects({ search: query, per_page: 5 })
                .then((response) => getResponseItems(response).map((project) => buildRecordResult({
                    type: t('Project'),
                    label: project.name || project.title || project.code || t('Project'),
                    path: `/project/project/${project.id}`,
                    icon: 'pi pi-list',
                    item: project
                })))
                .catch(() => [])
        );
    }

    if (canSearchPrograms.value) {
        requests.push(
            ProgramService.getPrograms({ search: query, per_page: 5 })
                .then((response) => getResponseItems(response).map((program) => buildRecordResult({
                    type: t('Program'),
                    label: program.name || program.title || program.code || t('Program'),
                    path: `/project/program/${program.id}`,
                    icon: 'pi pi-sitemap',
                    item: program
                })))
                .catch(() => [])
        );
    }

    // Strategic Plans record search is intentionally disabled for now.

    if (requests.length === 0) {
        dataSearchResults.value = [];
        return;
    }

    dataSearchLoading.value = true;
    const results = (await Promise.all(requests)).flat();

    if (requestId === searchRequestId) {
        dataSearchResults.value = results;
        dataSearchLoading.value = false;
    }
};

const closeSearch = () => {
    searchFocused.value = false;
    activeSearchIndex.value = -1;
};

const selectSearchResult = async (result) => {
    if (!result?.path) return;
    await router.push(result.path);
    search.value = '';
    closeSearch();
};

const submitSearch = () => {
    const result = filteredSearchResults.value[activeSearchIndex.value] || filteredSearchResults.value[0];
    if (result) {
        selectSearchResult(result);
    }
};

const handleSearchKeydown = (event) => {
    if (event.key === 'Escape') {
        event.preventDefault();
        closeSearch();
        return;
    }

    if (event.key === 'ArrowDown') {
        event.preventDefault();
        const lastIndex = filteredSearchResults.value.length - 1;
        if (lastIndex >= 0) {
            activeSearchIndex.value = activeSearchIndex.value >= lastIndex ? 0 : activeSearchIndex.value + 1;
        }
        return;
    }

    if (event.key === 'ArrowUp') {
        event.preventDefault();
        const lastIndex = filteredSearchResults.value.length - 1;
        if (lastIndex >= 0) {
            activeSearchIndex.value = activeSearchIndex.value <= 0 ? lastIndex : activeSearchIndex.value - 1;
        }
        return;
    }

    if (event.key === 'Enter') {
        event.preventDefault();
        submitSearch();
    }
};

const focusGlobalSearch = () => {
    const input = searchInputRef.value?.$el || searchInputRef.value;
    input?.focus?.();
    searchFocused.value = true;
};

const handleGlobalKeydown = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        focusGlobalSearch();
    }
};

const updateHeaderScrollState = () => {
    isScrolled.value = (scrollContainer?.scrollTop || 0) > 12;
};

const handleClickOutside = (event) => {
    if (!searchContainerRef.value?.contains(event.target)) {
        closeSearch();
    }
};

watch(search, (value) => {
    const query = value.trim();
    activeSearchIndex.value = -1;
    searchRequestId += 1;
    const requestId = searchRequestId;

    clearTimeout(searchDebounce.value);

    if (query.length < 2) {
        dataSearchLoading.value = false;
        dataSearchResults.value = [];
        return;
    }

    searchDebounce.value = setTimeout(() => {
        searchDataRecords(query, requestId);
    }, 250);
});

onMounted(() => {
    fetchUnreadCount();
    startRealtime(authStore.user?.id);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleGlobalKeydown);
    scrollContainer = headerRef.value?.parentElement || null;
    scrollContainer?.addEventListener('scroll', updateHeaderScrollState, { passive: true });
    updateHeaderScrollState();
});

onUnmounted(() => {
    stopRealtime();
    document.removeEventListener('mousedown', handleClickOutside);
    document.removeEventListener('keydown', handleGlobalKeydown);
    scrollContainer?.removeEventListener('scroll', updateHeaderScrollState);
    scrollContainer = null;
    clearTimeout(searchDebounce.value);
});
</script>

<template>
    <header
        ref="headerRef"
        :class="[
            'dashboard-header py-2 md:h-16 flex flex-col md:grid md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 border-b',
            { 'dashboard-header--scrolled': isScrolled },
            isRtl ? 'pl-4 pr-4' : 'pr-4',
            !isRtl && (calculatedMenuMode === 'overlay' || calculatedMenuMode === 'static')
                ? spacing.paddingLeft('4')
                : !isRtl ? spacing.paddingLeft('6') : ''
        ]"
    >
        <div :class="['dashboard-header__leading flex items-center md:justify-self-start min-w-0', getDirectionClasses('')]">
            <div :class="['md:flex items-center hidden', getDirectionClasses('')]">
                <template v-if="calculatedMenuMode === 'overlay' || calculatedMenuMode === 'static'">
                    <button type="button" :aria-label="t('Toggle navigation menu')" :title="t('Toggle navigation menu')" @click="toggleMenuState" class="header-icon-control cursor-pointer w-10 h-10 flex items-center justify-center">
                        <i class="pi pi-bars text-xl" aria-hidden="true" />
                    </button>
                    <Divider layout="vertical" />
                </template>
            </div>
            <div class="font-medium md:text-base text-xl">
                <!-- {{ pageTitle }} -->
                  <DashboardBreadcrumb />
            </div>
        </div>

        <!-- Search -->
        <div ref="searchContainerRef" class="dashboard-header__search spotlight-search relative hidden md:block md:justify-self-center w-[22rem] xl:w-[28rem]">
                <label
                    for="global-navigation-search"
                    class="sr-only"
                >
                    {{ t('Search') }}
                </label>
                <IconField class="w-full">
                    <InputIcon class="pi pi-search" />
                    <InputText
                        ref="searchInputRef"
                        id="global-navigation-search"
                        v-model="search"
                        :placeholder="t('Search')"
                        class="w-full"
                        autocomplete="off"
                        role="combobox"
                        :aria-expanded="showSearchResults"
                        aria-controls="global-search-results"
                        @focus="searchFocused = true"
                        @keydown="handleSearchKeydown"
                    />
                    <span class="spotlight-shortcut" aria-hidden="true">⌘ K</span>
                </IconField>

                <div
                    v-if="showSearchResults"
                    id="global-search-results"
                    role="listbox"
                    class="absolute top-full z-[1002] mt-2 w-full overflow-hidden rounded-xl border border-surface-200 bg-surface-0 shadow-xl dark:border-surface-700 dark:bg-surface-900"
                    :class="isRtl ? 'right-0' : 'left-0'"
                >
                    <div v-if="filteredSearchResults.length > 0" class="max-h-80 overflow-y-auto py-2">
                        <button
                            v-for="(result, index) in filteredSearchResults"
                            :key="result.path"
                            type="button"
                            role="option"
                            :aria-selected="activeSearchIndex === index"
                            class="search-result flex w-full items-center gap-3 px-4 py-3 transition-colors hover:bg-surface-100 focus:bg-surface-100 focus:outline-none dark:hover:bg-surface-800 dark:focus:bg-surface-800"
                            :class="{ 'search-result--active': activeSearchIndex === index }"
                            @mouseenter="activeSearchIndex = index"
                            @mousedown.prevent="selectSearchResult(result)"
                        >
                            <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-200">
                                <i :class="result.icon" class="!text-sm" />
                            </span>
                            <span class="search-result__copy min-w-0 flex-1">
                                <span class="block truncate text-sm font-semibold text-surface-800 dark:text-surface-100">{{ result.label }}</span>
                                <span class="block truncate text-xs text-surface-500 dark:text-surface-400">
                                    {{ [result.section, result.meta].filter(Boolean).join(' · ') }}
                                </span>
                            </span>
                            <i :class="isRtl ? 'pi pi-arrow-left' : 'pi pi-arrow-right'" class="search-result__arrow text-xs text-surface-400" />
                        </button>
                    </div>

                    <div v-else-if="dataSearchLoading" class="px-4 py-5 text-center text-sm text-surface-500 dark:text-surface-400">
                        {{ t('Searching...') }}
                    </div>

                    <div v-else class="px-4 py-5 text-center text-sm text-surface-500 dark:text-surface-400">
                        {{ t('No results found') }}
                    </div>
                </div>
        </div>
        <div class="dashboard-header__actions flex items-center gap-2 md:gap-4 md:justify-self-end">
                <button type="button" :aria-label="t('Toggle navigation menu')" :title="t('Toggle navigation menu')" @click="toggleMenuState" class="header-icon-control md:hidden cursor-pointer w-11 h-11 flex items-center justify-center">
                    <i class="pi pi-bars text-xl" aria-hidden="true" />
                </button>

                <!-- Language Switcher - Always visible -->
                <CompactLanguageSwitcher class="header-language-control" />

                <button
                    type="button"
                    :aria-label="t('Toggle theme')"
                    :title="t('Toggle theme')"
                    :aria-pressed="isDarkTheme"
                    @click="toggleDarkTheme"
                    class="header-icon-control relative w-10 h-10 flex items-center justify-center"
                >
                    <i :class="isDarkTheme ? 'pi pi-sun' : 'pi pi-moon'" class="!text-lg !leading-none" aria-hidden="true" />
                </button>

                <button
                    type="button"
                    :aria-label="t('Open appearance settings')"
                    :title="t('Open appearance settings')"
                    @click="toggleConfigSidebar"
                    class="header-icon-control relative w-10 h-10 flex items-center justify-center"
                >
                    <i class="pi pi-cog !text-lg !leading-none" aria-hidden="true" />
                </button>
                <AppConfig />
                <!-- Notifications -->

                <div class="relative">
                    <button type="button" :aria-label="t('Open notifications')" :title="t('Open notifications')" class="header-icon-control relative flex h-10 w-10 items-center justify-center" v-styleclass="{ selector: '@next', enterFromClass: 'hidden', enterActiveClass: 'animate-scalein', leaveActiveClass: 'animate-fadeout', leaveToClass: 'hidden', hideOnOutsideClick: true }">
                        <i class="pi pi-bell" aria-hidden="true" />
                        <span
                            v-if="unreadCount > 0"
                            class="absolute -top-1.5 inline-flex items-center justify-center min-w-5 h-5 px-1 text-[10px] font-bold text-white bg-red-500 rounded-full ring-2 ring-surface-0 dark:ring-surface-950 z-10"
                            :class="isRtl ? '-left-1.5 right-auto' : '-right-1.5 left-auto'"
                        >
                            {{ unreadCount > 99 ? '99+' : unreadCount }}
                        </span>
                    </button>
                    <div class="absolute hidden min-w-72 top-auto z-[999] mt-2" :class="isRtl ? 'left-0' : 'right-0'">
                        <NotificationsDropdown class="w-full sm:w-[22rem]" />
                    </div>
                </div>
                <!-- Profile -->
                <div class="relative">
                    <button type="button" :aria-label="t('Open profile menu')" :title="t('Open profile menu')" class="header-profile-control block border-0 bg-transparent p-0" v-styleclass="{ selector: '@next', enterFromClass: 'hidden', enterActiveClass: 'animate-scalein', leaveActiveClass: 'animate-fadeout', leaveToClass: 'hidden', hideOnOutsideClick: true }">
                        <img
                            :src="authStore.user?.avatar_url || defaultAvatar"
                            :alt="t('Profile')"
                            class="rounded-md w-8 h-8 cursor-pointer block object-cover"
                            width="32"
                            height="32"
                        />
                    </button>
                    <div class="absolute hidden top-full z-[999] mt-2" :class="isRtl ? 'left-0' : 'right-0'">
                        <ProfileDropdown class="w-52" />
                    </div>
                </div>

                <!-- <Button @click="toggleRightMenu" icon="pi pi-align-right" severity="secondary" outlined class="!aspect-square" /> -->
                <RightMenu :open="rightMenuOpen" @toggle="(value) => (rightMenuOpen = value)" />
        </div>
    </header>
</template>

<style scoped>
.dashboard-header {
    background: color-mix(in srgb, var(--sfh-surface-card, #fff) 96%, transparent);
    transition:
        background 240ms ease,
        border-color 240ms ease,
        box-shadow 240ms ease,
        backdrop-filter 240ms ease;
}

.dashboard-header--scrolled {
    border-color: color-mix(in srgb, var(--sfh-teal, #0095a3) 12%, transparent) !important;
    background: color-mix(in srgb, var(--sfh-surface-card, #fff) 72%, transparent) !important;
    box-shadow: 0 12px 30px rgba(14, 77, 79, .08), inset 0 -1px 0 rgba(255,255,255,.2);
    -webkit-backdrop-filter: blur(22px) saturate(150%);
    backdrop-filter: blur(22px) saturate(150%);
}

.spotlight-search :deep(.p-inputtext) {
    height: 3rem;
    padding-block: 0 !important;
    padding-inline-end: 3.5rem;
    border-color: color-mix(in srgb, var(--sfh-teal, #0095a3) 10%, transparent) !important;
    border-radius: 999px !important;
    line-height: normal;
    background: color-mix(in srgb, var(--sfh-surface-card, #fff) 92%, var(--sfh-teal-soft, #e5f4f6)) !important;
    box-shadow: 0 7px 20px rgba(14, 77, 79, .08), inset 0 0 0 1px rgba(255, 255, 255, .58);
}
.spotlight-search :deep(.p-inputtext:focus) {
    background: var(--sfh-surface-card, #fff) !important;
    box-shadow: var(--sfh-focus-ring, 0 0 0 3px rgba(0, 149, 163,.2)) !important;
}
.spotlight-search :deep(.p-iconfield) {
    display: flex;
    width: 100%;
    align-items: center;
}
.spotlight-search :deep(.p-inputicon) {
    position: absolute;
    inset-block-start: 50% !important;
    margin-block-start: 0 !important;
    display: inline-flex;
    width: 1.25rem;
    height: 1.25rem;
    align-items: center;
    justify-content: center;
    color: var(--sfh-text-muted, #7d8385);
    line-height: 1 !important;
    transform: translateY(-50%);
}
.spotlight-search :deep(.p-inputtext::placeholder) {
    color: var(--sfh-text-muted, #7d8385);
    line-height: normal;
    opacity: 1;
}
.search-result--active {
    background: color-mix(in srgb, var(--sfh-teal-soft, #e5f4f6) 78%, transparent);
}
.search-result {
    text-align: start !important;
}
.search-result__copy {
    display: block;
    text-align: start;
}
.search-result__arrow {
    flex: 0 0 auto;
    margin-inline-start: auto;
}
.spotlight-shortcut {
    position: absolute;
    inset-inline-end: .65rem;
    inset-block-start: 50%;
    transform: translateY(-50%);
    padding: .2rem .4rem;
    border: 1px solid var(--sfh-border-subtle, rgba(0,0,0,.06));
    border-radius: .4rem;
    color: var(--sfh-text-muted, #7d8385);
    background: var(--sfh-surface-card, #fff);
    font-size: .65rem;
    line-height: 1;
    display: inline-flex;
    min-height: 1.5rem;
    align-items: center;
    justify-content: center;
}
.header-icon-control {
    border: 1px solid var(--sfh-border-subtle, rgba(0,0,0,.06));
    border-radius: .75rem;
    color: var(--sfh-text-secondary, #666);
    background: var(--sfh-surface-card, #fff);
    box-shadow: 0 1px 2px rgba(0,0,0,.02);
    transition: transform 150ms cubic-bezier(.22,1,.36,1), background 150ms ease, color 150ms ease;
}
.header-icon-control:hover { color: var(--sfh-teal-dark, #007d89); background: var(--sfh-teal-soft, #e5f4f6); }
.header-icon-control:active { transform: scale(.96); }
.header-language-control {
    width: 2.5rem;
    height: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border: 1px solid var(--sfh-border-subtle, rgba(0,0,0,.06));
    border-radius: .75rem;
    color: var(--sfh-text-secondary, #666);
    background: var(--sfh-surface-card, #fff);
    box-shadow: 0 1px 2px rgba(0,0,0,.02);
    transition: transform 150ms cubic-bezier(.22,1,.36,1), background 150ms ease, color 150ms ease;
}
.header-language-control:hover { color: var(--sfh-teal-dark, #007d89); background: var(--sfh-teal-soft, #e5f4f6); }
.header-language-control:active { transform: scale(.96); }
.header-profile-control { border-radius: .8rem; transition: transform 150ms cubic-bezier(.22,1,.36,1); }
.header-profile-control:hover { transform: scale(1.04); }
.header-profile-control img { border-radius: .75rem; box-shadow: 0 0 0 2px var(--sfh-teal-soft, #e5f4f6); }
:global(.app-dark) .dashboard-header {
    background: color-mix(in srgb, var(--sfh-surface-card, #172121) 94%, transparent);
}
:global(.app-dark) .dashboard-header--scrolled {
    border-color: rgba(139, 219, 222, .12) !important;
    background: rgba(13, 27, 29, .72) !important;
    box-shadow: 0 14px 34px rgba(0,0,0,.22), inset 0 -1px 0 rgba(255,255,255,.04);
}
:global(.app-dark) .search-result--active {
    background: rgba(0, 149, 163, .14);
}
@media (max-width: 767px) { .spotlight-shortcut { display: none; } }
</style>
